import type { RootStore } from "@state/stores/types";
import { redirect } from "@state/utils";
import { getCurrentCulture } from "@utils";
import { action, computed, observable } from "mobx";
import CasinoStore from "./CasinoStore";
import LiveCasinoStore from "./LiveCasinoStore";

export default class CasinoSearchStore {
	minTermLength = 3;
	searchValidation = /^[A-Za-z0-9]{1,20}$/;

	rootStore: RootStore;
	casinoStore: CasinoStore | LiveCasinoStore | undefined;

	@observable searchTerm: string = "";
	@observable isSearchOpen: boolean = false;
	@observable isLiveCasino: boolean = false;
	@observable category: string = "";
	@observable isErrorOpen: boolean = false;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	@computed get redirectUrl(): string {
		if (this.isLiveCasino) {
			return `/${getCurrentCulture()}/live-games/search${this.category}?searchValue`;
		} else {
			return `/${getCurrentCulture()}/games/search${this.category}?searchValue`;
		}
	}

	@computed get isSearchLongEnough(): boolean {
		return this.searchTerm.trim().length >= this.minTermLength;
	}

	@action.bound
	onInitialize(
		isLiveCasino: boolean,
		casinoStore?: CasinoStore | LiveCasinoStore
	) {
		this.isLiveCasino = isLiveCasino;
		this.checkForCategory();
		this.casinoStore = casinoStore;
	}

	@action.bound
	checkForCategory() {
		const search = window.location.search;
		const params = new URLSearchParams(search);

		const categoryId = params.get("categoryId");

		this.category = categoryId ? `/${categoryId}` : "";
	}

	@action.bound
	setIsErrorOpen(value: boolean) {
		this.isErrorOpen = value;
	}

	@action.bound
	setIsSearchOpen(value: boolean) {
		this.isSearchOpen = value;
	}

	@action.bound
	searchButtonOnClick() {
		this.isSearchOpen = true;
		this.isErrorOpen = false;
	}

	@action.bound
	onSearchTermChange(term: string) {
		this.searchTerm = term;
	}

	@action.bound
	resetSearch() {
		this.searchTerm = "";
		this.setIsSearchOpen(false);
		this.setIsErrorOpen(false);
	}

	@action.bound
	onSubmit() {
		if (this.searchTerm !== "") {
			redirect(`${this.redirectUrl}="${this.searchTerm}"`);
		}

		this.resetSearch();
	}

	@action.bound
	onDispose() {
		this.searchTerm = "";
		this.isSearchOpen = false;
		this.isLiveCasino = false;
		this.category = "";
		this.casinoStore?.disposeBaseStore();
	}
}
